import { Injectable, inject } from '@angular/core';
import { Firestore, collection, collectionData, deleteDoc, doc, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { getStorage } from '@angular/fire/storage';
import { NetworkConstants, deepCopy, diffObject, removeUndefined, updatesToFirestoreUpdates } from 'diwimamodelweb/lib';
import { UserGroup } from 'diwimamodelweb/lib/network/user-group.data';
import { BehaviorSubject, Subject, Subscription, combineLatest, take, takeUntil } from 'rxjs';
import { SettingsService } from 'src/app/shared/settings.service';
import { TransactionLogService } from 'src/app/shared/transaction-log.service';
import { UserFeedbackService } from 'src/app/shared/user-feedback.service';
import { UserService } from 'src/app/shared/user.service';

@Injectable({
  providedIn: 'root'
})
export class UserGroupsService {
  private Ifirestore = inject(Firestore);
  storage = getStorage();

  ngUnsubscribe: Subject<void> = new Subject<void>();
  subscriptions: Map<string, Subscription> = new Map([])
  subbedContext: string|undefined = undefined

  allUserGroupsSubject$ = new BehaviorSubject<UserGroup[]|undefined>(undefined)
  allAssignableUserGroupsSubject$ = new BehaviorSubject<UserGroup[]|undefined>(undefined)
  private subbedAssignableContexts: string|undefined = undefined

  constructor(private settingsService: SettingsService,
    private userService: UserService,
    private transactionLogService: TransactionLogService) { }

  observeDataGroups(contextId) {
    console.log(' -- observeData() --', contextId, this.subbedContext)
    const subIds = this.settingsService.getSubcontextIds()
    const ids = this.settingsService.selectedContextAndSubContextIds.value
    const superOrsubContext = this.settingsService.superContext.value || this.settingsService.subContext.value
    const dataFromIds = superOrsubContext ? (ids ? ids?.join(',') : contextId) : contextId

    if (this.subbedContext == dataFromIds) return 
    if (this.subscriptions.get('1')) this.subscriptions.get('1').unsubscribe()
    if (!!this.allAssignableUserGroupsSubject$.value) this.allAssignableUserGroupsSubject$.next(undefined)

    if (superOrsubContext) {
      this.observeAssignableGroupsContextAndContextIds(contextId, subIds)
    } else {
      this.observeAssignableGroupsContext(contextId)
    }

    this.subbedContext = superOrsubContext ? dataFromIds : contextId
  }


  observeAssignableGroupsContextAndContextIds(contextId: string, contextIds: string[]) {
    const ids = [...new Set((contextIds ?? []).filter(Boolean))]
    console.log('-- observeAssignableGroupsContextAndContextIds ---', contextId, contextIds, ids, ids.length)

    const collRef = collection(this.Ifirestore, NetworkConstants.COLLECTION_USER_GROUP)
    const CHUNK_SIZE = 30 // Firestore's cap on `in`
    const qy$ = []

    qy$.push(collectionData(query(collRef, where('contextId', '==', contextId))));
    qy$.push(collectionData(query(collRef, where('contextIds', 'array-contains', contextId))));

    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const chunk = ids.slice(i, i + CHUNK_SIZE)
      qy$.push(collectionData(query(collRef, where('contextId', 'in', chunk))));
    }

    const sub = combineLatest(qy$)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (results: any[][]) => {
          const byId = new Map<string, UserGroup>()
          results.flat().forEach((el: any) => {
            const ug = new UserGroup()
            ug.setFromAny(el)
            const id = (ug as any)?.identifier ?? el?.identifier
            if (id) byId.set(id, ug)
          })
          this.allAssignableUserGroupsSubject$.next([...byId.values()])
        },
        error: (err) => console.error('Firestore error (assignable user groups):', err),
      })

    this.subscriptions.set('assignable', sub)
  }


  observeAssignableGroupsContext(contextId: string) {
    console.log('-- observeAssignableGroupsContext ---', contextId)

    const path = `${NetworkConstants.COLLECTION_USER_GROUP}`
    const collRef = collection(this.Ifirestore, path);
    const q = query(collRef, where('contextId', '==', contextId));

    return collectionData(q)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (results: any[][]) => {
          const byId = new Map<string, UserGroup>()
          results.flat().forEach((el: any) => {
            const ug = new UserGroup()
            ug.setFromAny(el)
            const id = (ug as any)?.identifier ?? el?.identifier
            if (id) byId.set(id, ug)
          })
          this.allAssignableUserGroupsSubject$.next([...byId.values()])
        },
        error: (err) => console.error('Firestore error (assignable user groups):', err),
      })
  }

  async removeSubs(): Promise<void> {
    this.ngUnsubscribe.next()
    this.subbedContext = undefined
    this.subbedAssignableContexts = undefined
    this.subscriptions.forEach((value) => {
      value.unsubscribe()
    })
    // reset loaded data
    // this.allUserGroupsSubject$.next(undefined)
    this.allAssignableUserGroupsSubject$.next(undefined)
    return Promise.resolve()
  }

  createUserGroup(newUsGroup: any): Promise<string> {
    return new Promise(async (resolve, reject) => {
      const docRef = doc(collection(this.Ifirestore, NetworkConstants.COLLECTION_USER_GROUP))
      const now = new Date()
      newUsGroup['identifier'] = docRef.id
      newUsGroup['creationDate'] = now
      newUsGroup['timestamp'] = now
      try {
        const contextId = this.settingsService.selectedUserContext$.value.identifier
        if (!contextId) reject("No context id")
        const user = await this.userService.user$.pipe(take(1)).toPromise()
        const logItems = this.transactionLogService.createLog(user, newUsGroup.identifier, NetworkConstants.COLLECTION_USER_GROUP, newUsGroup)
        newUsGroup['contextId'] = contextId

        await setDoc(docRef, {...newUsGroup} )
        await this.transactionLogService.saveTransactionLog(logItems)
        resolve(docRef.id)
      } catch (error) {
        reject(error)
      }
    })
  }

  deleteUserGroup(usg: UserGroup): Promise<void>{
    return new Promise(async (resolve, reject) => {
      try{
        const groupToDelete = deepCopy(usg, usg.replacerFn)
        const dataToDelete = removeUndefined(groupToDelete)
        const user = await this.userService.user$.pipe(take(1)).toPromise()
        const transactionLogItem = this.transactionLogService.deleteDocumentLog(
          user,
          usg.identifier,
          NetworkConstants.COLLECTION_USER_GROUP,
          dataToDelete
        )
        const promiseArray = []
        promiseArray.push(
          this.transactionLogService.saveTransactionLog([transactionLogItem])
        )
        const docRef = doc(this.Ifirestore, NetworkConstants.COLLECTION_USER_GROUP, usg.identifier)
        const delOperation = await deleteDoc( docRef)
        promiseArray.push(delOperation)
        resolve()
      }catch(error){
        reject(error)
        console.log(error)
      }
    })
  }

  updateUserGroup(oldUsGr: UserGroup, newUsGr: UserGroup): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const updates = diffObject(oldUsGr, newUsGr, {})
      const map = updatesToFirestoreUpdates(updates)
      try {
        const user = await this.userService.user$.pipe(take(1)).toPromise()
        const list = await this.transactionLogService.determineDiff(user, updates, newUsGr.identifier, NetworkConstants.COLLECTION_USER_GROUP)
        map['timestamp'] = new Date()
        const docRef = doc(this.Ifirestore, NetworkConstants.COLLECTION_USER_GROUP, newUsGr.identifier)
        await updateDoc( docRef, map)
        // if successfull save transactionlog..
        await this.transactionLogService.saveTransactionLog(list)
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

}
